import { supabase } from '../lib/supabase';
import { getRandomObjectColor } from '../utils/objectColors';
/**
 * Service for managing studies and study-clinician assignments
 * Most methods require admin privileges and will throw errors if called by non-admins
 */
export class StudyService {
    /**
     * Get all studies with clinician counts
     * @returns Array of studies with clinician count
     * @throws Error if user is not an admin or database error occurs
     */
    static async getAllStudies() {
        const { data, error } = await supabase.rpc('get_all_studies');
        if (error) {
            console.error('Error fetching studies:', error);
            throw new Error(error.message || 'Unable to fetch studies');
        }
        return data || [];
    }
    /**
     * Get a specific study by ID
     * @param studyId - The ID of the study
     * @returns The study
     * @throws Error if study not found or database error occurs
     */
    static async getStudyById(studyId) {
        const { data, error } = await supabase
            .from('studies')
            .select('*')
            .eq('id', studyId)
            .single();
        if (error) {
            console.error('Error fetching study:', error);
            // If it's a "not found" error, return null
            if (error.code === 'PGRST116') {
                return null;
            }
            // For other errors, throw
            throw new Error(error.message || 'Unable to fetch study');
        }
        return data;
    }
    /**
     * Create a new study
     * @param title - The title of the study
     * @param description - Optional description
     * @param createdBy - The ID of the admin creating the study
     * @returns The created study
     * @throws Error if user is not an admin or database error occurs
     */
    static async createStudy(title, description, createdBy, color) {
        const { data, error } = await supabase
            .from('studies')
            .insert({
            title,
            description: description || null,
            created_by: createdBy,
            color: color || getRandomObjectColor()
        })
            .select()
            .single();
        if (error) {
            console.error('Error creating study:', error);
            throw new Error(error.message || 'Unable to create study');
        }
        return data;
    }
    /**
     * Get clinicians assigned to a specific study
     * @param studyId - The ID of the study
     * @returns Array of clinicians with assignment metadata
     * @throws Error if user is not an admin or database error occurs
     */
    static async getStudyClinicians(studyId) {
        const { data, error } = await supabase.rpc('get_study_clinicians', {
            p_study_id: studyId
        });
        if (error) {
            console.error('Error fetching study clinicians:', error);
            throw new Error(error.message || 'Unable to fetch study clinicians');
        }
        return data || [];
    }
    /**
     * Get clinicians not assigned to a specific study
     * Useful for populating the "assign clinician" dropdown
     * @param studyId - The ID of the study
     * @returns Array of unassigned clinicians
     * @throws Error if user is not an admin or database error occurs
     */
    static async getUnassignedClinicians(studyId) {
        // Get all clinicians from the database
        const { data: allClinicians, error: cliniciansError } = await supabase
            .from('user_profiles')
            .select('*')
            .contains('roles', ['clinician'])
            .order('last_name', { ascending: true })
            .order('first_name', { ascending: true });
        if (cliniciansError) {
            console.error('Error fetching all clinicians:', cliniciansError);
            throw new Error(cliniciansError.message || 'Unable to fetch clinicians');
        }
        // Get clinicians already assigned to this study
        const assignedClinicians = await this.getStudyClinicians(studyId);
        const assignedClinicianIds = new Set(assignedClinicians.map(c => c.id));
        // Filter out assigned clinicians
        return (allClinicians || []).filter((clinician) => !assignedClinicianIds.has(clinician.id));
    }
    /**
     * Assign a clinician to a study
     * @param studyId - The ID of the study
     * @param clinicianId - The ID of the clinician
     * @returns The created assignment
     * @throws Error if user is not an admin, assignment already exists, or database error occurs
     */
    static async assignClinicianToStudy(studyId, clinicianId) {
        const { data, error } = await supabase
            .from('study_clinician_assignments')
            .insert({
            study_id: studyId,
            clinician_id: clinicianId
        })
            .select()
            .single();
        if (error) {
            console.error('Error assigning clinician to study:', error);
            // Check if it's a unique constraint violation
            if (error.code === '23505') {
                throw new Error('This clinician is already assigned to this study');
            }
            throw new Error(error.message || 'Unable to assign clinician to study');
        }
        return data;
    }
    /**
     * Remove a clinician from a study by assignment ID
     * @param assignmentId - The ID of the assignment
     * @throws Error if user is not an admin or database error occurs
     */
    static async removeClinicianFromStudy(assignmentId) {
        const { error } = await supabase
            .from('study_clinician_assignments')
            .delete()
            .eq('id', assignmentId);
        if (error) {
            console.error('Error removing clinician from study:', error);
            throw new Error(error.message || 'Unable to remove clinician from study');
        }
    }
    /**
     * Get studies assigned to a specific clinician
     * @param clinicianId - The ID of the clinician
     * @returns Array of studies with assignment metadata
     * @throws Error if user is not authorized or database error occurs
     */
    static async getClinicianStudies(clinicianId) {
        const { data, error } = await supabase.rpc('get_clinician_studies', {
            p_clinician_id: clinicianId
        });
        if (error) {
            console.error('Error fetching clinician studies:', error);
            throw new Error(error.message || 'Unable to fetch clinician studies');
        }
        return data || [];
    }
    /**
     * Delete a study
     * @param studyId - The ID of the study to delete
     * @throws Error if user is not an admin or database error occurs
     */
    static async deleteStudy(studyId) {
        const { error } = await supabase.from('studies').delete().eq('id', studyId);
        if (error) {
            console.error('Error deleting study:', error);
            throw new Error(error.message || 'Unable to delete study');
        }
    }
    /**
     * Update a study
     * @param studyId - The ID of the study to update
     * @param title - The new title
     * @param description - The new description
     * @param color - The new color
     * @returns The updated study
     * @throws Error if user is not an admin or database error occurs
     */
    static async updateStudy(studyId, title, description, color) {
        const updateData = {
            title,
            description: description || null,
            color
        };
        const { data, error } = await supabase
            .from('studies')
            .update(updateData)
            .eq('id', studyId)
            .select()
            .single();
        if (error) {
            console.error('Error updating study:', error);
            throw new Error(error.message || 'Unable to update study');
        }
        return data;
    }
    /**
     * Get all patients who have used devices in a specific study
     * @param studyId - The ID of the study
     * @returns Array of patients with their device usage information
     * @throws Error if user is not an admin or database error occurs
     */
    static async getStudyPatients(studyId) {
        const { data, error } = await supabase.rpc('get_study_patients', {
            p_study_id: studyId
        });
        if (error) {
            console.error('Error fetching study patients:', error);
            throw new Error(error.message || 'Unable to fetch study patients');
        }
        return data || [];
    }
}
